import { Button, Checkbox } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";

import { api } from "../../lib/apiClient";

/**
 * Kayıt ekranındaki hukuki onay kutuları (CLOS-342).
 *
 * **Her belge AYRI kutudur.** Tek bir "hepsini kabul ediyorum" kutusu, kullanıcının
 * pazarlama iznini fark etmeden vermesine yol açardı — oysa kart açık: pazarlama izni
 * zorunlu onaylardan ayrı tutulur ve verilmemesi kaydı bloke etmez.
 *
 * Metinler ve sürümler SUNUCUDAN gelir; istemci sabit bir sürüm bilmez. Bilseydi, metin
 * güncellendiğinde eski sürümü onaylatmaya devam ederdi.
 */
export type LegalDocument = {
  id: string;
  type: string;
  version: number;
  locale: string;
  required: boolean;
};

/** Kullanıcıya görünen adlar. Tür kapalı küme olduğu için burada da kapalı. */
const BASLIKLAR: Record<string, string> = {
  terms_of_service: "Kullanım Şartları",
  privacy_policy: "Gizlilik Politikası",
  marketing: "Kampanya ve duyuru e-postaları almak istiyorum",
};

function baslik(type: string): string {
  return BASLIKLAR[type] ?? type;
}

export function useLegalDocuments() {
  return useQuery({
    queryKey: ["session", "legal-documents"],
    queryFn: async () => (await api<LegalDocument[]>("/api/session/legal-documents")).data,
  });
}

/**
 * Kullanıcının onaylaması GEREKEN ama onaylamadığı belgeler (CLOS-343).
 *
 * Hem kapı (`RequireCurrentConsents`) hem de yeniden onay ekranı AYNI sorguyu kullanır;
 * ikisi farklı kaynaklardan beslenseydi kapı "eksik var" derken ekran boş kalabilirdi.
 */
export function usePendingConsents() {
  return useQuery({
    queryKey: ["session", "pending-consents"],
    queryFn: async () => (await api<LegalDocument[]>("/api/session/pending-consents")).data,
  });
}

export function LegalConsentFields({
  belgeler,
  secilenler,
  degistir,
  hata,
  yukleniyor,
  yuklenemedi,
  yenidenDene,
}: {
  belgeler: LegalDocument[];
  secilenler: string[];
  degistir: (id: string, secili: boolean) => void;
  hata?: string;
  yukleniyor?: boolean;
  yuklenemedi?: boolean;
  yenidenDene?: () => void;
}) {
  /**
   * ⚠ ÜÇ DURUM AYRI (kusur avı, 26 Ağustos).
   *
   * Bileşen eskiden yalnız `belgeler.length === 0` bakıyor ve `null` dönüyordu: sorgu
   * hata verdiğinde onay alanı hiç çizilmiyor, kullanıcı Kayıt Ol'a basıyor, sunucu
   * "onay eksik" diyor ve ekranda işaretlenecek HİÇBİR kutu olmuyordu. Hatayı gören
   * ama düzeltemeyen bir kullanıcı, çıkmazdaki bir kullanıcıdır.
   *
   * "Gerçekten belge yok" hâlâ `null`: o zaman onaylanacak bir şey de yoktur.
   */
  if (yuklenemedi) {
    return (
      <div role="alert" className="flex flex-col items-start gap-2 text-sm text-danger">
        <p>Onay metinleri yüklenemedi. Onaylamadan hesap açılamaz.</p>
        {yenidenDene ? (
          <Button variant="ghost" size="sm" onClick={yenidenDene} className="-ml-3.5">
            Yeniden dene
          </Button>
        ) : null}
      </div>
    );
  }

  if (yukleniyor) {
    return (
      <p role="status" className="text-sm text-fg-muted">
        Onay metinleri yükleniyor…
      </p>
    );
  }

  if (belgeler.length === 0) return null;

  return (
    <fieldset className="flex flex-col gap-2">
      {/* Grup etiketi: ekran okuyucu kutuların neye ait olduğunu duyurabilsin. */}
      <legend className="text-sm font-medium text-fg-strong">Onaylar</legend>

      {belgeler.map((belge) => (
        <Checkbox
          key={belge.id}
          checked={secilenler.includes(belge.id)}
          onChange={(e) => degistir(belge.id, e.target.checked)}
          label={
            belge.required ? (
              /*
                ⚠ TEK `span` — parçalar doğrudan etikete verilemez. `Checkbox`'ın `label`
                elemanı `inline-flex gap-2`; buraya konan her çocuk ayrı bir flex öğesi
                olur ve aralarına 8px girer. Parçalar ayrı verildiğinde ekranda
                "Gizlilik Politikası 'nı okudum ve kabul ediyorum  *" diye kopuk
                çiziliyordu — hatta görünmez `sr-only` metin bile kendi boşluğunu
                tüketiyordu. Tek kapta parçalar normal satır akışına döner.
              */
              <span>
                {/* Bağlantı AYRI bir odak hedefi: kullanıcı kutuyu işaretlemeden önce
                    metni okuyabilmeli ve klavyeyle oraya ulaşabilmeli. */}
                <a
                  href={`/legal/${belge.type}?v=${belge.version}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary hover:underline"
                >
                  {baslik(belge.type)}
                </a>
                {"'nı okudum ve kabul ediyorum"}
                <span aria-hidden="true"> *</span>
                <span className="sr-only"> (zorunlu)</span>
              </span>
            ) : (
              baslik(belge.type)
            )
          }
        />
      ))}

      {hata ? (
        <p role="alert" className="text-sm text-danger">
          {hata}
        </p>
      ) : null}
    </fieldset>
  );
}
